import { isValidElement } from "react";
import { EmptyState, ErrorState, PageLoading, Skeleton, timeET, etSyd } from "@/sentinel";

// One way to show a query's state on every evidence page: loading → skeleton,
// error → ErrorState (retry + last good data time), empty → EmptyState. Errors are never shown as "not run yet".

/** { status, code, message } from an axios error. `code` is the contract's detail.code (e.g. NO_SESSION). */
export function apiError(err) {
  if (!err) return null;
  const status = err.response?.status ?? null;
  const detail = err.response?.data?.detail;
  const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code || null : null;
  let message;
  if (typeof detail === "string") message = detail;
  else if (detail && typeof detail === "object" && !Array.isArray(detail)) message = detail.message || detail.msg || code;
  else if (Array.isArray(detail)) message = detail.map((d) => d?.msg).filter(Boolean).join(" ");
  if (!message) message = status ? `The server answered ${status}.` : err.message === "Network Error" ? "The server could not be reached." : err.message;
  return { status, code, message };
}

/** GET /v1/sessions/{date} 404 → no session recorded for this date (contract: NO_SESSION). */
export const isNoSession = (err) => {
  const e = apiError(err);
  return !!e && e.status === 404 && (e.code === "NO_SESSION" || e.code == null);
};

/** react-query `retry`: retry network and 5xx errors twice, never a 4xx. */
export const retryTransient = (count, err) => {
  const s = err?.response?.status;
  if (s && s >= 400 && s < 500) return false;
  return count < 2;
};

/** "Updated 10:01:22 ET" for a react-query dataUpdatedAt. */
export function UpdatedAt({ at, prefix = "Updated", ...rest }) {
  if (!at) return null;
  return <span className="sn-small sn-faint" {...rest}>{prefix} <span className="sn-num">{etSyd(at)}</span></span>;
}

/** The error panel for a failed query, with Retry and the time of the last good data (if any). */
export function QueryError({ query, title = "This panel could not load", what = "the data", testId }) {
  const e = apiError(query?.error);
  const last = query?.dataUpdatedAt ? etSyd(query.dataUpdatedAt) : null;
  return (
    <div data-testid={testId}>
      <ErrorState title={title} onRetry={() => query?.refetch?.()} detail={e?.message}>
        {`Sentinel could not load ${what}. This is a failed request, not an empty session.`}
        {last ? ` Showing nothing newer than ${last}.` : ""}
      </ErrorState>
    </div>
  );
}

/**
 * Renders a query's loading / error / empty states, then `children(data)`.
 * - `isEmpty(data)` decides the empty state (default: null, [] or {}).
 * - `empty` is the EmptyState props ({ icon, title, body, testId }) or a node.
 * - `loading`: "page" (PageLoading) or a number of skeleton lines.
 * - An error with data still cached keeps the data and shows the error above it (stale data stays visible).
 */
export function QueryBoundary({ query, isEmpty, empty, loading = 3, errorTitle, what, errorTestId, children }) {
  const { data, isLoading, isError } = query;
  if (isLoading) return loading === "page" ? <PageLoading /> : <Skeleton lines={loading} />;
  if (isError && data === undefined) return <QueryError query={query} title={errorTitle} what={what} testId={errorTestId} />;
  const emptyNow = isEmpty ? isEmpty(data) : data == null || (Array.isArray(data) && data.length === 0);
  const body = emptyNow
    ? (isValidElement(empty) ? empty : (
      <EmptyState icon={empty?.icon || "Layers"} title={empty?.title || "Nothing to show yet"} data-testid={empty?.testId}>
        {empty?.body}
      </EmptyState>
    ))
    : children(data);
  return (
    <>
      {isError && <QueryError query={query} title={errorTitle || "Refresh failed · showing the last good data"} what={what} testId={errorTestId} />}
      {body}
    </>
  );
}
